// JustEasySEO — Design-system-page (M144 Fase 1).
// Alleen zichtbaar voor test-orgs OF met `?dev=1` query-param. Toont alle
// merkcomponenten in hun states + contrast-check-tabel.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import {
  Card, KpiCard, ProposalCard, PageHeader, StatusBadge,
  EmptyState, LoadingState, ErrorState,
} from "@/components/ui/JeCard";
import { runContrastCheck } from "@/theme/contrast";
import { colors } from "@/theme/tokens";
import { TrendingUp, Users, Sparkles, ArrowRight, Palette, Search, Inbox } from "lucide-react";

function Swatch({ name, hex }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md border border-border-hairline bg-bg-panel">
      <div className="w-10 h-10 rounded-md" style={{ background: hex, border: "1px solid var(--border-hairline)" }} />
      <div className="text-xs">
        <div className="font-medium text-text-primary">{name}</div>
        <div className="text-text-muted font-mono">{hex}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-text-primary mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function DesignSystemPage() {
  const { activeOrg } = useAuth();
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const devQ = q.get("dev") === "1";
    const isTest = !!activeOrg?.is_test_org;
    setAllowed(devQ || isTest);
  }, [activeOrg]);
  const contrast = useMemo(() => runContrastCheck(), []);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-bg-canvas flex items-center justify-center p-6">
        <Card>
          <div className="text-text-primary font-semibold mb-2">
            Deze pagina is alleen voor testomgevingen.
          </div>
          <div className="text-sm text-text-muted mb-4">
            Open de link met <code>?dev=1</code> of log in met een test-organisatie.
          </div>
          <Link to="/" className="text-accent-teal hover:underline text-sm">Terug naar overzicht</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-canvas text-text-primary">
      <div className="max-w-[1200px] mx-auto p-6 md:p-10">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <Logo variant="horizontal" size={40} showTagline />
          <Link to="/" className="text-sm text-accent-teal hover:underline">Naar overzicht →</Link>
        </div>

        <PageHeader
          title="Design system"
          subtitle="Alle merkcomponenten in hun states. Deze pagina is alleen voor test-omgevingen."
        />

        <Section title="Logo">
          <div className="flex flex-wrap gap-6 items-center bg-bg-panel p-6 rounded-card border border-border-hairline">
            <Logo variant="horizontal" size={48} showTagline />
            <Logo variant="horizontal" size={32} />
            <Logo variant="mark" size={48} />
            <Logo variant="mono" size={40} />
          </div>
          <div className="bg-[#172C32] p-6 rounded-card">
            <div className="text-white text-xs mb-3">Op donkere achtergrond (voor exports):</div>
            <Logo variant="mono" size={40} />
          </div>
        </Section>

        <Section title="Kleurpalet">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Swatch name="Warm wit (canvas)" hex={colors.bgCanvas} />
            <Swatch name="Wit (kaarten)"     hex={colors.bgPanel} />
            <Swatch name="Inktblauw (tekst)" hex={colors.textPrimary} />
            <Swatch name="Petrol (hoofd)"    hex={colors.accentPetrol} />
            <Swatch name="Petrol-hover"      hex={colors.accentPetrolHover} />
            <Swatch name="Mint (steun)"      hex={colors.accentMint} />
            <Swatch name="Abrikoos (accent)" hex={colors.accentApricot} />
            <Swatch name="Succes"            hex={colors.posGreen} />
            <Swatch name="Waarschuwing"      hex={colors.warnAmber} />
            <Swatch name="Fout"              hex={colors.negRed} />
            <Swatch name="Rand"              hex={colors.borderHairline} />
            <Swatch name="Secundaire tekst"  hex={colors.textMuted} />
          </div>
        </Section>

        <Section title="Contrast-check (WCAG)">
          <Card padding="none">
            <table className="w-full text-sm">
              <thead className="bg-bg-panel-raised">
                <tr className="text-left text-xs text-text-muted">
                  <th className="px-4 py-2">Combinatie</th>
                  <th className="px-4 py-2">Grootte</th>
                  <th className="px-4 py-2">Gemeten</th>
                  <th className="px-4 py-2">Nodig</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {contrast.map((r, i) => (
                  <tr key={i} className="border-t border-border-hairline">
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-4 h-4 rounded" style={{ background: r.bg, border: "1px solid var(--border-hairline)" }} />
                        <span style={{ color: r.fg, background: r.bg, padding: "0 6px", borderRadius: 4 }}>{r.label}</span>
                        <span className="text-text-subtle">{r.bgLabel}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-text-muted">{r.size === "large" ? "Groot" : "Normaal"}</td>
                    <td className="px-4 py-2 tabular-nums">{r.ratio}:1</td>
                    <td className="px-4 py-2 tabular-nums text-text-muted">{r.need}:1</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.pass ? "connected" : "error"}
                                    label={r.pass ? "PASS" : "FAIL"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Section>

        <Section title="Knoppen">
          <div className="flex flex-wrap gap-3">
            <Button variant="primary">Primair</Button>
            <Button variant="secondary">Secundair</Button>
            <Button variant="tertiary">Tertiair</Button>
            <Button variant="destructive">Verwijderen</Button>
            <Button variant="primary" disabled>Uitgeschakeld</Button>
            <Button variant="primary" loading>Bezig…</Button>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <Button variant="primary" size="sm">Klein</Button>
            <Button variant="primary" size="md">Normaal</Button>
            <Button variant="primary" size="lg">Groot</Button>
            <Button variant="primary" leftIcon={<Sparkles className="w-4 h-4" />}>Met icoon</Button>
          </div>
        </Section>

        <Section title="Statusbadges">
          <div className="flex flex-wrap gap-3 items-center">
            <StatusBadge status="disconnected" />
            <StatusBadge status="connecting" />
            <StatusBadge status="connected" />
            <StatusBadge status="reauth" />
            <StatusBadge status="waiting" />
            <StatusBadge status="error" />
          </div>
        </Section>

        <Section title="KPI-kaarten">
          <div className="grid md:grid-cols-3 gap-4">
            <KpiCard icon={TrendingUp} label="Websitebezoeken" value="1.284"
                     subline="+12% t.o.v. vorige periode" tone="positive"
                     source="GA4" period="Laatste 30 dagen" lastSync="1u geleden" />
            <KpiCard icon={Users} label="Aanvragen" value="—" empty
                     emptyHint="Koppel een formulier om aanvragen te tellen." />
            <KpiCard icon={Search} label="Google-positie" value="7,2"
                     subline="Gemiddelde positie top-10 pagina's"
                     source="Search Console" period="Laatste 4 weken" tone="neutral" />
          </div>
        </Section>

        <Section title="Voorstel-kaart">
          <ProposalCard
            title="Pas de meta-omschrijving van de service-pagina aan"
            what="Verduidelijk in 155 tekens wat de bezoeker krijgt en waar."
            why="Uw pagina heeft veel vertoningen maar weinig kliks."
            basis="Search Console, laatste 4 weken · 1.834 vertoningen · CTR 1.1%"
            kosten="10 minuten van uw contentmedewerker."
            volgendeStap="Kopieer de nieuwe tekst en plaats 'm in WordPress."
            onOpen={() => {}}
          />
        </Section>

        <Section title="Empty / Loading / Error">
          <EmptyState icon={Inbox} title="Nog geen berichten"
                       body="Zodra er een advies klaarstaat verschijnt 'ie hier."
                       action={<Button variant="primary" size="sm">Maak eerste bericht</Button>} />
          <LoadingState label="Adviezen laden…" />
          <ErrorState title="Kon niet laden"
                      body="Probeer het opnieuw. Blijft het probleem? Neem contact op."
                      onRetry={() => {}} />
        </Section>

        <Section title="Typografie">
          <Card>
            <h1 className="text-3xl font-semibold mb-2">Titel — 32px semibold</h1>
            <h2 className="text-2xl font-semibold mb-2">Sub-titel — 28px semibold</h2>
            <h3 className="text-xl font-semibold mb-2">Sectiekop — 24px semibold</h3>
            <p className="text-base text-text-primary mb-2">
              Body 16px — dit is de basisregel. Rustige leestekst met genoeg regelhoogte.
            </p>
            <p className="text-sm text-text-muted mb-2">Ondersteunend 14px — bijschriften.</p>
            <p className="text-xs text-text-subtle">Klein 12px — voor labels en meta-info.</p>
          </Card>
        </Section>

        <div className="text-xs text-text-subtle pt-4 border-t border-border-hairline">
          JustEasySEO — design-system · M144 Fase 1 · alleen zichtbaar in test-omgevingen.
        </div>
      </div>
    </div>
  );
}
